import { useState, useRef, useEffect } from "react";
import { Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useSearchCertificateHolders } from "@/lib/api";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { CertificateHolder } from "@shared/schema";

interface CertificateHolderPickerProps {
  selectedId: string | null;
  holderName: string;
  holderAddress: string;
  onSelect: (holder: CertificateHolder) => void;
  onClear: () => void;
  onNameChange: (name: string) => void;
  onAddressChange: (address: string) => void;
}

// CSR-745: picker queries the live `certificate_holders` table via
// `useSearchCertificateHolders` (debounced 250ms). The legacy
// `coi_issuances`-aggregated history source has been retired (T11).
export function CertificateHolderPicker({
  selectedId,
  holderName,
  holderAddress,
  onSelect,
  onClear,
  onNameChange,
  onAddressChange,
}: CertificateHolderPickerProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebouncedValue(searchQuery, 250);
  const { data: holders = [] } = useSearchCertificateHolders(debouncedQuery, showDropdown);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (val: string) => {
    setSearchQuery(val);
    onNameChange(val);
    if (!showDropdown && val.length > 0) setShowDropdown(true);
    if (selectedId) onClear();
  };

  // Two-line item: primary = name, secondary = address (or contactEmail
  // if address is missing). Right-side badge = usageCount. Reference: the
  // legacy "Previously Used" UI used a single line with a clock icon;
  // CSR-745 swaps that for a small Building2 icon and an address line.
  const renderSecondary = (holder: CertificateHolder): string => {
    if (holder.address && holder.address.trim().length > 0) return holder.address;
    if (holder.contactEmail && holder.contactEmail.trim().length > 0) return holder.contactEmail;
    return "";
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        placeholder="Type a holder name..."
        value={searchQuery || holderName}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setShowDropdown(true)}
        data-testid="input-holder-search"
      />
      {showDropdown && holders.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 top-full mt-1 left-0 right-0 bg-popover border rounded-md shadow-lg max-h-[260px] overflow-y-auto"
        >
          <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
            Address Book
          </div>
          <div className="p-1 pt-0">
            {holders.map((holder) => {
              const secondary = renderSecondary(holder);
              return (
                <button
                  key={holder.id}
                  type="button"
                  className="w-full text-left px-3 py-2 rounded-sm text-sm hover:bg-accent hover:text-accent-foreground flex items-start gap-2 cursor-pointer"
                  onClick={() => {
                    setSearchQuery(holder.name);
                    setShowDropdown(false);
                    onSelect(holder);
                  }}
                  data-testid={`holder-option-${holder.id}`}
                >
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{holder.name}</div>
                    {secondary && (
                      <div className="truncate text-[11px] text-muted-foreground">{secondary}</div>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0 mt-0.5">
                    {holder.usageCount}x
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
